import { useEffect, useState } from "react";

import { formatDate } from "@usas/ui-web/utils/DateFunctions";

const INITIAL_SELECTED_ATHLETES_STATE = {
  athletesToSync: [],
  athletesToTransfer: []
};

const useClubAthleteRosterFormGrid = (gridData, onSubmitAthleteFilter, onSubmitAthleteSync, onSubmitAthleteTransfer,
  onErrorCaught) => {
  const [formGridState, setFormGridState] = useState([]);
  const [selectedAthletesState, setSelectedAthletesState] = useState(INITIAL_SELECTED_ATHLETES_STATE);
  const [displayTransferPopupState, setDisplayTransferPopupState] = useState(false);

  const onClearFilterClicked = (event, formState) => {
    try {
      event?.preventDefault();

      setFormGridState(createFormGridState(gridData, [], []));
      setSelectedAthletesState({ athletesToSync: [], athletesToTransfer: [] });

      onSubmitAthleteFilter(formState);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onSyncSelectedClicked = (event) => {
    try {
      event?.preventDefault();

      const newFormGridState = createFormGridState(gridData, [], selectedAthletesState.athletesToTransfer);
      setFormGridState(newFormGridState);
      setSelectedAthletesState(createSelectedAthletesState(newFormGridState, [], selectedAthletesState.athletesToTransfer));

      onSubmitAthleteSync(selectedAthletesState.athletesToSync);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onTransferSelectedClicked = (event) => {
    try {
      event?.preventDefault();

      setDisplayTransferPopupState(true);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onTransferPopupConfirmClicked = (event) => {
    try {
      event?.preventDefault();

      const newFormGridState = createFormGridState(gridData, selectedAthletesState.athletesToSync, [])

      setFormGridState(newFormGridState);
      setSelectedAthletesState(createSelectedAthletesState(newFormGridState, selectedAthletesState.athletesToSync, []));

      setDisplayTransferPopupState(false);
      onSubmitAthleteTransfer(selectedAthletesState.athletesToTransfer);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onTransferPopupCancelClicked = (event) => {
    try {
      event?.preventDefault();

      setDisplayTransferPopupState(false);
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };


  const onUpdateIsSyncSelected = (memberId) => {
    try {
      const newFormGridState = JSON.parse(JSON.stringify(formGridState));

      for (let i = 0; i < newFormGridState.length; i++) {
        if (newFormGridState[i].memberId === memberId) {
          newFormGridState[i].isSyncSelected = !newFormGridState[i].isSyncSelected;

          break;
        }
      }

      setFormGridState(newFormGridState);
      setSelectedAthletesState(createSelectedAthletesState(newFormGridState, selectedAthletesState.athletesToSync, selectedAthletesState.athletesToTransfer));
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  const onUpdateIsTransferSelected = (personOrgRoleDurationId) => {
    try {
      const newFormGridState = JSON.parse(JSON.stringify(formGridState));

      for (let i = 0; i < newFormGridState.length; i++) {
        if (newFormGridState[i].personOrgRoleDurationId === personOrgRoleDurationId) {
          newFormGridState[i].isTransferSelected = !newFormGridState[i].isTransferSelected;

          break;
        }
      }

      setFormGridState(newFormGridState);
      setSelectedAthletesState(createSelectedAthletesState(newFormGridState, selectedAthletesState.athletesToSync, selectedAthletesState.athletesToTransfer));
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
  };

  useEffect(() => {
    try {
      const newFormGridState = createFormGridState(gridData, selectedAthletesState.athletesToSync, selectedAthletesState.athletesToTransfer);

      setFormGridState(newFormGridState);
      setSelectedAthletesState(createSelectedAthletesState(newFormGridState, selectedAthletesState.athletesToSync, selectedAthletesState.athletesToTransfer));
    } catch (e) {
      onErrorCaught ? onErrorCaught(e) : () => { throw e };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData]);

  function createFormGridState(gridData, athletesToSync = [], athletesToTransfer = []) {
    const newFormGridState = [];

    for (const clubAthleteObj of gridData) {
      const newClubAthleteFormObj = createClubAthleteFormObj(clubAthleteObj, athletesToSync, athletesToTransfer);

      newFormGridState.push(newClubAthleteFormObj);
    }

    return newFormGridState;
  }

  function createClubAthleteFormObj(clubAthleteObj, athletesToSync = [], athletesToTransfer = []) {
    return {
      personId: clubAthleteObj.personId,
      memberId: clubAthleteObj.memberId,
      unClubId: clubAthleteObj.unClubId,
      personOrgRoleDurationId: clubAthleteObj.personOrgRoleDurationId,
      firstName: clubAthleteObj.firstName,
      preferredName: clubAthleteObj.preferredName,
      lastName: clubAthleteObj.lastName,
      competitionGenderTypeName: clubAthleteObj.competitionGenderTypeName,
      age: clubAthleteObj.age >= 0 ? `${clubAthleteObj.age}` : '',
      birthDate: clubAthleteObj.birthDate ? formatDate(clubAthleteObj.birthDate) : '',
      isInGoodStandingExpirationDate: clubAthleteObj.isInGoodStandingExpirationDate ? formatDate(clubAthleteObj.isInGoodStandingExpirationDate) : '',
      orgRoleName: clubAthleteObj.orgRoleName,
      attachDate: clubAthleteObj.attachDate ? formatDate(clubAthleteObj.attachDate) : '',
      isTransferPending: clubAthleteObj.isTransferPending === true,
      isSyncSelected: athletesToSync.some((x) => x.memberId === clubAthleteObj.memberId) === true,
      isTransferSelected: clubAthleteObj.isTransferPending !== true ? athletesToTransfer.some((x) => x.personOrgRoleDurationId === clubAthleteObj.personOrgRoleDurationId) === true : false
    };
  }

  function createSelectedAthletesState(formGridState, athletesToSync = [], athletesToTransfer = []) {
    const newSelectedAthletesState = {
      athletesToSync: [...athletesToSync],
      athletesToTransfer: [...athletesToTransfer]
    };

    for (const clubAthleteObj of formGridState) {
      if (clubAthleteObj.isSyncSelected === true) {
        const athlete = {
          memberId: clubAthleteObj.memberId,
          name: `${clubAthleteObj.preferredName || clubAthleteObj.firstName} ${clubAthleteObj.lastName}`
        };

        if (athletesToSync.some((x) => x.memberId === athlete.memberId) === false) {
          newSelectedAthletesState.athletesToSync.push(athlete);
        }
      } else {
        const athleteIndex = athletesToSync.findIndex((x) => x.memberId === clubAthleteObj.memberId);

        if (athleteIndex > -1) {
          newSelectedAthletesState.athletesToSync.splice(athleteIndex, 1);
        }
      }

      if (clubAthleteObj.isTransferSelected === true) {
        const athlete = {
          personOrgRoleDurationId: clubAthleteObj.personOrgRoleDurationId,
          unClubId: clubAthleteObj.unClubId,
          name: `${clubAthleteObj.preferredName || clubAthleteObj.firstName} ${clubAthleteObj.lastName}`
        };

        if (athletesToTransfer.some((x) => x.personOrgRoleDurationId === athlete.personOrgRoleDurationId) === false) {
          newSelectedAthletesState.athletesToTransfer.push(athlete);
        }
      } else {
        const athleteIndex = athletesToTransfer.findIndex((x) => x.personOrgRoleDurationId === clubAthleteObj.personOrgRoleDurationId);

        if (athleteIndex > -1) {
          newSelectedAthletesState.athletesToTransfer.splice(athleteIndex, 1);
        }
      }
    }

    return newSelectedAthletesState;
  }

  return {
    displayTransferPopup: displayTransferPopupState,
    formGridState,
    selectedAthletesState,
    onClearFilterClicked,
    onSyncSelectedClicked,
    onTransferSelectedClicked,
    onTransferPopupConfirmClicked,
    onTransferPopupCancelClicked,
    onUpdateIsSyncSelected,
    onUpdateIsTransferSelected
  };
};

export default useClubAthleteRosterFormGrid;